import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { buildApiUrl } from '../config/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(buildApiUrl('/contact/contact'), formData);

      setFormStatus({
        submitted: true,
        success: !!response.data?.success || true,
        message: response.data?.message || 'Thank you for contacting us! We will get back to you within 24 hours.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setFormStatus({
        submitted: true,
        success: false,
        message: error.response?.data?.message || 'Failed to send your message. Please try again.'
      });
    } finally {
      setSubmitting(false);
      setTimeout(() => {
        setFormStatus({
          submitted: false,
          success: false,
          message: ''
        });
      }, 5000);
    }
  };

  const contactInfo = [
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Visit Us',
      details: ['Prayagraj (Head Office)', 'Noida Office'],
      action: 'View Locations',
      link: 'https://maps.app.goo.gl/XvWTDJY4YNFfQ8HRA'
    },
    {
      icon: 'fas fa-phone-alt',
      title: 'Call Us',
      details: ['+91 9335444047', '+91 7895300727'],
      action: 'Call Now',
      link: 'tel:+919335444047'
    },
    {
      icon: 'fas fa-envelope',
      title: 'Email Us',
      details: ['info@redgotrips.com', 'bookings@redgotrips.com'],
      action: 'Send Email',
      link: 'mailto:info@redgotrips.com'
    },
    {
      icon: 'fas fa-clock',
      title: 'Working Hours',
      details: ['Mon-Sat: 9AM - 8PM', 'Sun: 10AM - 4PM'],
      action: '24/7 Support',
      link: '#'
    }
  ];

  const socialMedia = [
    { icon: 'fab fa-facebook-f', name: 'Facebook', link: 'https://facebook.com/redgotrip', color: '#1877f2' },
    { icon: 'fab fa-instagram', name: 'Instagram', link: 'https://instagram.com/redgotrip', color: '#e4405f' },
    { icon: 'fab fa-twitter', name: 'Twitter', link: 'https://twitter.com/redgotrip', color: '#1da1f2' },
    { icon: 'fab fa-whatsapp', name: 'WhatsApp', link: 'https://wa.me/919335444047', color: '#25d366' }
  ];

  const offices = [
    {
      city: 'Prayagraj',
      address: '388 B H S, Shantipuram, Allahabad, Uttar Pradesh - 211006',
      phone: '+919335444047',
      email: 'info@redgotrips.com',
    },
    {
      city: 'Noida Office',
      address: 'P5/522, NPX URBTECH, Sector 153, Noida, Uttar Pradesh - 201310',
      phone: '+917235097873',
      email: 'sales@redgotrips.com',
    }
  ];

  return (
    <div className="contact-page">
      <SEO
        title="Contact RedGoTrip | Travel Help & Support"
        description="Contact RedGoTrip for package booking help, camping queries, custom holiday planning, payments and travel support."
        canonicalPath="/contact"
        keywords="contact RedGoTrip, travel support, package booking help, camping support"
      />
      {/* Hero Section - Compact */}
      <section className="contact-hero">
        <div className="hero-content">
          <h1>Get in Touch</h1>
          <p>We'd love to hear from you. Let us help plan your perfect Holidays!</p>
        </div>
      </section>

      {/* Contact Info Cards - Optimized Grid */}
      <section className="contact-info-section">
        <div className="container">
          <div className="info-grid">
            {contactInfo.map((info, index) => (
              <div key={index} className="info-card">
                <div className="info-icon">
                  <i className={info.icon}></i>
                </div>
                <h3>{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="info-detail">{detail}</p>
                ))}
                <a href={info.link} className="info-action">
                  {info.action} <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content - Compact Grid */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Left Column - Map & Offices */}
            <div className="left-column">
              {/* Map - Compact */}
              <div className="map-container">
                <div className="map-header">
                  <h2>Our Location</h2>
                </div>
                <div className="map-wrapper">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3626.6567743408496!2d83.08179007498839!3d24.635511854222717!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399ab5f0a5e679ab%3A0xbd2fabd1d0784ef5!2sRedgotrip!5e0!3m2!1sen!2sin!4v1775640565169!5m2!1sen!2sin"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="RedGoTrip Location"
                  ></iframe>
                </div>
              </div>

              {/* Office Locations - Compact */}
              <div className="offices-compact">
                {offices.map((office, index) => (
                  <div key={index} className="office-card">
                    <h4>{office.city}</h4>
                    <p><i className="fas fa-map-pin"></i> {office.address}</p>
                    <div className="office-contact">
                      <a href={`tel:${office.phone}`}><i className="fas fa-phone"></i> {office.phone}</a>
                      <a href={`mailto:${office.email}`}><i className="fas fa-envelope"></i> {office.email}</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="form-container">
              <div className="form-header">
                <h2>Send Message</h2>
                <p>We'll respond within 24 hours</p>
              </div>

              {formStatus.submitted && (
                <div className={formStatus.success ? 'success-message' : 'error-message'}>
                  <i className={formStatus.success ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'}></i>
                  <p>{formStatus.message}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name *"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address *"
                  />
                </div>

                <div className="form-row">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                  />
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="Subject *"
                  />
                </div>

                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Your Message *"
                ></textarea>

                <div className="form-footer">
                  <label className="checkbox-label">
                    <input type="checkbox" required />
                    <span>I agree to <Link to="/privacy">Privacy Policy</Link></span>
                  </label>
                  <button type="submit" className="submit-btn" disabled={submitting}>
                    {submitting ? (
                      <>Sending <i className="fas fa-spinner fa-spin"></i></>
                    ) : (
                      <>Send <i className="fas fa-paper-plane"></i></>
                    )}
                  </button>
                </div>
              </form>

              {/* Social Links - Compact */}
              <div className="social-compact">
                {socialMedia.map((social, index) => (
                  <a
                    key={index}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    style={{ backgroundColor: social.color }}
                  >
                    <i className={social.icon}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <h3>Ready for Holidays?</h3>
            <div className="cta-buttons">
              <a href="tel:+919335444047" className="cta-btn primary">
                <i className="fas fa-phone-alt"></i> Call Now
              </a>
              <a href="https://wa.me/919335444047" className="cta-btn whatsapp">
                <i className="fab fa-whatsapp"></i> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .contact-page {
          background: #f8fafc;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }

        /* Hero Section */
        .contact-hero {
          background: linear-gradient(135deg, #334f7f 0%, #764ba2 100%);
          padding: 6rem 1rem 6rem;
          text-align: center;
          color: white;
        }

        .contact-hero h1 {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .contact-hero p {
          font-size: 1.1rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
        }

        /* Contact Info Cards */
        .contact-info-section {
          margin-top: -2rem;
          position: relative;
          z-index: 10;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }

        .info-card {
          background: white;
          padding: 1.5rem 1rem;
          border-radius: 10px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
          text-align: center;
          transition: transform 0.3s;
        }

        .info-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(102,126,234,0.1);
        }

        .info-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #334f7f 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .info-icon i {
          font-size: 1.2rem;
          color: white;
        }

        .info-card h3 {
          font-size: 1rem;
          margin-bottom: 0.5rem;
          color: #1e293b;
        }

        .info-detail {
          color: #64748b;
          font-size: 0.85rem;
          line-height: 1.4;
        }

        .info-action {
          display: inline-block;
          margin-top: 0.75rem;
          color: #ec3134;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 500;
        }

        /* Main Content */
        .contact-main {
          padding: 3rem 0;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        /* Left Column */
        .left-column {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .map-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        }

        .map-header {
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .map-header h2 {
          font-size: 1.1rem;
          color: #1e293b;
          margin: 0;
        }

        .map-wrapper {
          padding: 1rem;
        }

        .map-wrapper iframe {
          border-radius: 8px;
          height: 250px;
        }

        /* Offices Compact */
        .offices-compact {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .office-card {
          background: white;
          padding: 1rem;
          border-radius: 10px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        }

        .office-card h4 {
          font-size: 0.95rem;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .office-card p {
          color: #64748b;
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .office-card i {
          color: #ec3134;
          width: 16px;
          font-size: 0.8rem;
        }

        .office-contact {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }

        .office-contact a {
          color: #334f7f;
          text-decoration: none;
          font-size: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .office-contact a:hover {
          color: #ec3134;
        }

        /* Form Container */
        .form-container {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }

        .form-header {
          margin-bottom: 1.5rem;
        }

        .form-header h2 {
          font-size: 1.3rem;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .form-header p {
          color: #64748b;
          font-size: 0.9rem;
        }

        .success-message {
          background: #dcfce7;
          color: #166534;
          padding: 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .contact-form input,
        .contact-form textarea {
          width: 100%;
          padding: 0.75rem 1rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.3s;
          background: #f8fafc;
        }

        .contact-form input:focus,
        .contact-form textarea:focus {
          outline: none;
          border-color: #334f7f;
          background: white;
        }

        .form-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 0.5rem;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.85rem;
          cursor: pointer;
        }

        .checkbox-label a {
          color: #ec3134;
          text-decoration: none;
        }

        .checkbox-label a:hover {
          text-decoration: underline;
        }

        .submit-btn {
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #334f7f 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(51,79,127,0.2);
        }

        /* Social Compact */
        .social-compact {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          margin-top: 1.5rem;
        }

        .social-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          text-decoration: none;
          transition: transform 0.3s;
        }

        .social-icon:hover {
          transform: translateY(-3px) scale(1.1);
        }

        /* CTA Section */
        .contact-cta {
          padding: 2rem 0;
          background: linear-gradient(135deg, #334f7f 0%, #764ba2 100%);
        }

        .cta-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .cta-content h3 {
          color: white;
          font-size: 1.2rem;
          margin: 0;
        }

        .cta-buttons {
          display: flex;
          gap: 0.75rem;
        }

        .cta-btn {
          padding: 0.6rem 1.2rem;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
        }

        .cta-btn.primary {
          background: white;
          color: #334f7f;
        }

        .cta-btn.whatsapp {
          background: #25d366;
          color: white;
        }

        .cta-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .info-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .offices-compact {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .cta-content {
            flex-direction: column;
            text-align: center;
          }

          .cta-buttons {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 1rem;
          }

          .form-footer {
            flex-direction: column;
            gap: 1rem;
          }

          .checkbox-label {
            justify-content: center;
          }

          .submit-btn {
            width: 100%;
            justify-content: center;
          }

          .social-compact {
            justify-content: center;
          }

          .cta-buttons {
            flex-direction: column;
            width: 100%;
          }

          .cta-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
